/**
 * 節點可用性編排 — 前端 API client（BO redesign P2 寫入 wire）。
 *
 * 對應後端：`POST /api/admin/node-availability`
 *   （admin.node_availability.controller.ts → node_availability.service.ts）。
 * 設計依據：docs/operations/backoffice-redesign-2026-06-27.md §5.2 / §5.4 / §7.3。
 *
 * 🔴 紅線（前端只是把後端契約呈現給 operator，真正 enforce 在後端）：
 *  - targetBrand 走 **顯式 body 欄位**（不靠 activeBrand）。authFetch 會額外帶 `X-Brand:
 *    activeBrand`，但後端 `resolveAdminWriteTargetBrand` 只認顯式 `targetBrand` /
 *    `X-Target-Brand`，不被 `X-Brand` 蓋過 → 故這裡用 body.targetBrand 即可，避免歧義。
 *  - default-deny：對 targetBrand 不可見的節點要曝光時，**後端**要求
 *    `confirmExpandBrandExposure=true` 才寫；缺旗標回 `code≠0` + 結構化 `blocked` envelope。
 *    前端據此彈紅色二次確認，**不自作主張預設帶旗標**（旗標只有 operator 在二次確認後才送）。
 */

import { authFetch } from '../../utils/api';
import type { Brand } from '../../utils/tokenManager';
import type { CellStatus } from './effective';

export interface OrchestrateInput {
  nodeIds: number[];
  targetBrand: Brand;
  addToTierIds: number[];
  grantBrandVisibility: boolean;
  confirmExpandBrandExposure: boolean;
}

export interface BlockedEnvelope {
  reason: 'CROSS_BRAND_EXPOSURE_REQUIRES_CONFIRM';
  message: string;
  invisibleNodes: Array<{
    id: number;
    name: string;
    allowedBrands: Brand[] | null;
  }>;
}

export interface EffectivePreviewRow {
  nodeId: number;
  name: string;
  status: CellStatus;
  shared: boolean;
  reasons: string[];
  qualifyingTierIds: number[];
}

export interface OrchestrateApplied {
  exposureGranted: Array<{
    nodeId: number;
    before: Brand[] | null;
    after: Brand[];
  }>;
  tierAdditions: Array<{ tierId: number; tierName: string; added: number[] }>;
  tiersSkipped: Array<{ tierId: number; reason: string }>;
}

export interface OrchestrateResult {
  ok: boolean;
  blocked?: BlockedEnvelope;
  applied?: OrchestrateApplied;
  effective: EffectivePreviewRow[];
}

/** API 回應外層（標準 `{ code, msg, data }`）。 */
export interface OrchestrateResponse {
  /** code===0 ＝ 寫入成功；code≠0 ＝ 被擋（多半是 default-deny），data 仍帶 OrchestrateResult。 */
  code: number;
  msg: string;
  data: OrchestrateResult | null;
}

/**
 * 呼叫編排端點。回傳 normalize 後的 `{ code, msg, data }`。
 * - code===0：成功，data.applied / data.effective 有值。
 * - code!==0 且 data.blocked：default-deny 擋下（需 operator 二次確認後重送帶旗標）。
 * - code!==0 且無 data.blocked：其他錯誤（驗證 / 授權 / 後端例外），呈現 msg。
 */
export async function orchestrateNodeAvailability(
  input: OrchestrateInput
): Promise<OrchestrateResponse> {
  const res = await authFetch('/api/admin/node-availability', {
    method: 'POST',
    headers: {
      // 顯式 targetBrand header（與 body 一致；後端 first-match-wins，body 優先，這裡多帶一層保險）。
      'X-Target-Brand': input.targetBrand,
    },
    body: JSON.stringify({
      nodeIds: input.nodeIds,
      targetBrand: input.targetBrand,
      addToTierIds: input.addToTierIds,
      grantBrandVisibility: input.grantBrandVisibility,
      confirmExpandBrandExposure: input.confirmExpandBrandExposure,
    }),
  });

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    return {
      code: 1,
      msg: `伺服器回應無法解析（HTTP ${res.status}）`,
      data: null,
    };
  }

  const obj = (json ?? {}) as Record<string, unknown>;
  const code = typeof obj.code === 'number' ? obj.code : 1;
  const msg = typeof obj.msg === 'string' ? obj.msg : '操作失敗';
  const data = (obj.data ?? null) as OrchestrateResult | null;
  return { code, msg, data };
}
